import AdminOnly from '@/components/ui/AdminOnly';
import { FileName, FolderPath, Iso } from '@/components/ui/FileName';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity as ActivityIcon, ChevronDown, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { type ActivityRow } from '@/lib/queries';

interface Props {
  queryKey: readonly unknown[];
  fetcher: () => Promise<ActivityRow[]>;
  emptyHint?: string;
  /** Rows shown before the "Show all" cut. */
  previewCount?: number;
  /** localStorage key for remembering the open/closed choice per surface. */
  storageKey?: string;
}

export default function ActivityFeed({
  queryKey,
  fetcher,
  emptyHint = 'No activity yet.',
  previewCount = 5,
  storageKey = 'activityFeed.open',
}: Props) {
  const { data, isLoading } = useQuery({ queryKey, queryFn: fetcher });

  // Collapsed state is remembered per surface. Reading localStorage can throw
  // (private mode, blocked site data), so every access is guarded and simply
  // falls back to the default rather than taking the panel down with it.
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? true : v === '1';
    } catch {
      return true;
    }
  });
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, open ? '1' : '0');
    } catch {
      /* storage unavailable - the choice just does not persist */
    }
  }, [open, storageKey]);

  const rows = data ?? [];
  const total = rows.length;
  const visible = expanded ? rows : rows.slice(0, previewCount);
  const hidden = total - visible.length;

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 mb-3 text-left group"
      >
        {open
          ? <ChevronDown size={14} className="text-muted group-hover:text-muted-strong" />
          : <ChevronRight size={14} className="text-muted group-hover:text-muted-strong" />}
        <ActivityIcon size={16} className="text-accent-400" />
        <h3 className="font-medium text-muted-strong text-sm">Activity</h3>
        <span className="text-xs text-muted ml-auto">{total}</span>
      </button>

      {open && (
        <>
          {isLoading && <div className="text-xs text-muted">Loading...</div>}
          {!isLoading && total === 0 && <div className="text-xs text-muted py-3">{emptyHint}</div>}

          {!isLoading && total > 0 && (
            <>
              <ol className="relative border-l border-slate-800 ml-2 pl-4 space-y-3">
                {visible.map((a) => (
                  <li key={a.id} className="text-xs">
                    <span className="absolute -left-[5px] mt-1 w-2.5 h-2.5 rounded-full bg-accent-400 border-2 border-bg-card" />
                    <div className="text-muted-strong">
                      <span className="text-accent-400">{a.actor_name}</span>{' '}
                      <span className="text-muted">{a.verb.replace(/_/g, ' ')}</span>{' '}
                      <Iso className="text-muted-strong">{a.target_title ?? a.target_type}</Iso>
                    </div>
                    <div className="text-muted text-[10px] mt-0.5">{fmt(a.at)}</div>
                    {/* Raw before/after fields: diagnostics for the owner, noise for the team. */}
                    {a.meta && Object.keys(a.meta).length > 0 && (
                      <AdminOnly label="details" className="mt-1"><div className="text-[10px] text-muted space-y-0.5">
                        {Object.entries(a.meta).map(([k, v]) => {
                          // Two meta shapes: per-field diff `{old, new}`, or a plain key/value scalar.
                          if (v && typeof v === 'object' && 'old' in v && 'new' in v) {
                            const diff = v as { old: unknown; new: unknown };
                            return (
                              <div key={k} className="font-mono">
                                <span className="text-muted-strong">{k}:</span>{' '}
                                <span className="text-red-400/70 line-through">{String(diff.old ?? '')}</span>{' '}
                                <span className="text-muted">-&gt;</span>{' '}
                                <span className="text-emerald-400">{String(diff.new ?? '')}</span>
                              </div>
                            );
                          }
                          if (v == null) return null;  // skip nulls so we don't render "key: null" noise
                          return (
                            <div key={k} className="font-mono">
                              <span className="text-muted-strong">{k}:</span>{' '}
                              {k === 'filename' ? <FileName name={String(v)} className="text-muted" />
                                : k === 'folder' ? <FolderPath path={String(v)} className="text-muted" />
                                : <Iso className="text-muted">{String(v)}</Iso>}
                            </div>
                          );
                        })}
                      </div></AdminOnly>
                    )}
                  </li>
                ))}
              </ol>

              {(hidden > 0 || expanded) && (
                <button
                  type="button"
                  onClick={() => setExpanded((e) => !e)}
                  className="mt-3 text-xs text-accent-400 hover:underline"
                >
                  {expanded ? 'Show less' : `Show all ${total}`}
                </button>
              )}
            </>
          )}
        </>
      )}
    </Card>
  );
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString('en-AE', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Dubai' });
}
